import type { TocItem } from '@/components/case-study/caseStudyDefaultToc'
import { CASE_STUDY_SECTION_IDS } from '@/domain/caseStudySectionAnchors'
import type { CaseStudyLong } from '@/domain/longCaseStudy'

type SpecialThankYouBlock = {
  heading?: string
  body?: string
  people?: { name: string; role?: string; photo_url?: string; link_url?: string; link_label?: string }[]
}

const anchorToSectionId = (anchor: string): string => (anchor.startsWith('#') ? anchor.slice(1) : anchor)

/** Omitted from the Content Sheet: end matter, or sections no longer on the page. */
export const CONTENT_SHEET_EXCLUDED_SECTION_IDS = new Set<string>([
  CASE_STUDY_SECTION_IDS.author,
  CASE_STUDY_SECTION_IDS.specialThankYou,
  CASE_STUDY_SECTION_IDS.readAlso,
  CASE_STUDY_SECTION_IDS.closing,
  /** Meta-only field (`seo_summary.body`); not rendered on the page. */
  CASE_STUDY_SECTION_IDS.seo,
  /** Legacy section removed from the page; drop from custom TOC if still present. */
  'component-demo',
])

/** Keep Content Sheet links aligned with sections that actually render. */
export const isCaseStudyTocSectionVisible = (
  sectionId: string,
  data: CaseStudyLong,
  specialThankYouBlock: SpecialThankYouBlock | undefined,
): boolean => {
  switch (sectionId) {
    case CASE_STUDY_SECTION_IDS.hero:
      return Boolean(data.page?.title?.trim())
    case CASE_STUDY_SECTION_IDS.tldr:
      return Boolean(data.tldr?.body?.trim())
    case CASE_STUDY_SECTION_IDS.quote:
      return Boolean(data.quote?.body?.trim())
    case CASE_STUDY_SECTION_IDS.problem:
      return Boolean(data.problem?.paragraphs?.length)
    case CASE_STUDY_SECTION_IDS.oldWorkflow:
      return Boolean(data.old_workflow?.steps?.length || data.old_workflow?.intro?.trim())
    case CASE_STUDY_SECTION_IDS.reframing:
      return Boolean(data.reframing?.paragraphs?.length)
    case CASE_STUDY_SECTION_IDS.solution:
      return Boolean(
        data.solution?.paragraphs?.length ||
          (data.solution?.execution_steps && data.solution.execution_steps.length > 0),
      )
    case CASE_STUDY_SECTION_IDS.workflowSchema:
      return Boolean(data.workflow_schema?.body?.trim())
    case CASE_STUDY_SECTION_IDS.beforeAfter:
      return Boolean(data.before_after?.rows?.length)
    case CASE_STUDY_SECTION_IDS.impact:
      return Boolean(data.impact?.paragraphs?.length)
    case CASE_STUDY_SECTION_IDS.transferability:
      return Boolean(data.transferability?.paragraphs?.length)
    case CASE_STUDY_SECTION_IDS.gift:
      return Boolean(data.gift?.body?.trim())
    case CASE_STUDY_SECTION_IDS.faq:
      return Boolean(data.faq?.items?.length)
    case CASE_STUDY_SECTION_IDS.author:
      return Boolean(data.author?.name?.trim() || data.author?.bio_paragraphs?.length)
    case CASE_STUDY_SECTION_IDS.specialThankYou:
      return Boolean(specialThankYouBlock?.people?.length)
    case CASE_STUDY_SECTION_IDS.readAlso:
      return Boolean(data.read_also?.items?.length)
    default:
      return true
  }
}

export const filterCaseStudyTocItems = (
  items: TocItem[],
  data: CaseStudyLong,
  specialThankYouBlock: SpecialThankYouBlock | undefined,
): TocItem[] =>
  items.filter((item) => {
    const sectionId = anchorToSectionId(item.anchor)
    if (CONTENT_SHEET_EXCLUDED_SECTION_IDS.has(sectionId)) {
      return false
    }
    return isCaseStudyTocSectionVisible(sectionId, data, specialThankYouBlock)
  })

